import { memo, useMemo, useCallback } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import LineLoader from '@components/ui/LineLoader/LineLoader';
import styles from './DataTable.module.scss';

// ============================================
// TYPES
// ============================================

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  /** Column id to sort by */
  columnId: string;
  /** Sort direction */
  direction: SortDirection;
}

export type ColumnAlignment = 'left' | 'center' | 'right';

/** Controlled pagination state derived from a Spring Pageable response. */
export interface PaginationState {
  /** Current page number (0-based for Spring Pageable) */
  page: number;
  /** Items per page */
  size: number;
  /** Total number of items across all pages */
  totalElements: number;
  /** Total number of pages */
  totalPages: number;
}

/** Column definition for a DataTable. Generic over the row data type. */
export interface DataTableColumn<TRow> {
  /** Unique identifier for this column */
  id: string;
  /** Header label displayed in the thead */
  header: string;
  /** Render function for each cell in this column */
  cell: (row: TRow) => ReactNode;
  /** CSS width value (e.g. '200px', '30%'). Omit for auto width. */
  width?: string;
  /** Text alignment for both header and body cells. Defaults to 'left'. */
  align?: ColumnAlignment;
  /** Whether this column supports sorting */
  sortable?: boolean;
  /**
   * API field name used for server-side sorting.
   * Falls back to `id` if not provided.
   */
  sortKey?: string;
  /** Additional CSS class applied to body cells in this column */
  className?: string;
  /** Additional CSS class applied to the header cell */
  headerClassName?: string;
}

/** Props for the DataTable component. Generic over the row data type. */
export interface DataTableProps<TRow> {
  /** Column definitions describing headers and cell rendering */
  columns: DataTableColumn<TRow>[];
  /** Array of row data to display */
  data: TRow[];
  /** Extracts a unique key from each row for React reconciliation */
  rowKey: (row: TRow) => string | number;
  /** Optional click handler invoked when a row is clicked */
  onRowClick?: (row: TRow) => void;
  /** When true, shows a loading indicator instead of the table body */
  loading?: boolean;
  /** Text shown during loading state. Defaults to 'Loading...' */
  loadingText?: string;
  /** Content rendered when data is empty and not loading */
  emptyState?: ReactNode;
  /** Controlled sort state */
  sort?: SortState;
  /** Called when a sortable column header is clicked. Receives `undefined` on third click to clear sort. */
  onSortChange?: (sort: SortState | undefined) => void;
  /** Controlled pagination state. When provided, renders a pagination footer. */
  pagination?: PaginationState;
  /** Called when the user navigates to a different page (0-based page index). */
  onPageChange?: (page: number) => void;
  /** Called when the user changes the page size. Parent should also reset page to 0. */
  onPageSizeChange?: (size: number) => void;
  /** Options shown in the page-size dropdown. Defaults to [10, 20, 50, 100]. */
  pageSizeOptions?: number[];
  /** Whether to show the toolbar with the filter icon placeholder. */
  showToolbar?: boolean;
  /** Called when the filter toolbar button is clicked */
  onFilterClick?: () => void;
  /** Number of active filters — shows a badge on the filter icon */
  activeFilterCount?: number;
  /** When true, shows a thin indeterminate loading bar at the top. Does not replace table body. */
  fetching?: boolean;
  /**
   * Render function for row-level actions shown as a hover overlay
   * on the last column. Return `null` to show no actions for a given row.
   */
  rowActions?: (row: TRow) => ReactNode;
  /** Additional CSS class for the outermost container */
  className?: string;
  /** Accessible label for the table element */
  'aria-label'?: string;
}

// ============================================
// CONSTANTS
// ============================================

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

// ============================================
// ICONS
// ============================================

const SORT_ICON_SIZE = 14;

const SortIcon = memo(function SortIcon({
  direction,
  active,
}: {
  direction?: SortDirection;
  active: boolean;
}) {
  return (
    <svg
      className={`${styles.sortIcon} ${active ? styles.sortIconActive : ''}`}
      width={SORT_ICON_SIZE}
      height={SORT_ICON_SIZE}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Up arrow */}
      <path
        d="M7 2L10 5.5H4L7 2Z"
        fill="currentColor"
        opacity={active && direction === 'asc' ? 1 : 0.3}
      />
      {/* Down arrow */}
      <path
        d="M7 12L4 8.5H10L7 12Z"
        fill="currentColor"
        opacity={active && direction === 'desc' ? 1 : 0.3}
      />
    </svg>
  );
});

/** Filter funnel icon for toolbar placeholder. */
const FilterIcon = memo(function FilterIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M18.333 2.5H1.667L8.333 10.383V15.833L11.667 17.5V10.383L18.333 2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

const NAV_ICON_SIZE = 16;

/** Left chevron for pagination prev button. */
const ChevronLeftIcon = memo(function ChevronLeftIcon() {
  return (
    <svg
      width={NAV_ICON_SIZE}
      height={NAV_ICON_SIZE}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10 12L6 8L10 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

/** Right chevron for pagination next button. */
const ChevronRightIcon = memo(function ChevronRightIcon() {
  return (
    <svg
      width={NAV_ICON_SIZE}
      height={NAV_ICON_SIZE}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 12L10 8L6 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

// ============================================
// COMPONENT
// ============================================

function DataTable<TRow>({
  columns,
  data,
  rowKey,
  onRowClick,
  loading = false,
  loadingText = 'Loading...',
  emptyState,
  sort,
  onSortChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS as unknown as number[],
  showToolbar = false,
  onFilterClick,
  activeFilterCount,
  rowActions,
  fetching = false,
  className,
  'aria-label': ariaLabel,
}: DataTableProps<TRow>) {
  const { t } = useTranslation();

  const containerClass = useMemo(
    () => [styles.container, className].filter(Boolean).join(' '),
    [className],
  );

  // Pre-compute header styles per column (avoids object creation in render loop)
  // `width` is applied as maxWidth to constrain long-content columns (name/description)
  const headerStyleMap = useMemo(() => {
    const map = new Map<string, CSSProperties>();
    for (const col of columns) {
      if (col.width || col.align) {
        const style: CSSProperties = {};
        if (col.width) style.maxWidth = col.width;
        if (col.align) style.textAlign = col.align;
        map.set(col.id, style);
      }
    }
    return map;
  }, [columns]);

  // Pre-compute cell styles per column (avoids rows × columns object allocations)
  const cellStyleMap = useMemo(() => {
    const map = new Map<string, CSSProperties>();
    for (const col of columns) {
      const style: CSSProperties = {};
      if (col.width) style.maxWidth = col.width;
      if (col.align) style.textAlign = col.align;
      if (Object.keys(style).length > 0) map.set(col.id, style);
    }
    return map;
  }, [columns]);

  // Track which columns have width (need truncation)
  const truncatedColumns = useMemo(() => {
    const set = new Set<string>();
    for (const col of columns) {
      if (col.width) set.add(col.id);
    }
    return set;
  }, [columns]);

  // Pre-compute header class strings per column
  const headerClassMap = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const col of columns) {
      const cls =
        [
          col.sortable ? styles.sortableHeader : undefined,
          col.width ? styles.truncatedCell : undefined,
          col.headerClassName,
        ]
          .filter(Boolean)
          .join(' ') || undefined;
      map.set(col.id, cls);
    }
    return map;
  }, [columns]);

  const handleHeaderClick = useCallback(
    (column: DataTableColumn<TRow>) => {
      if (!column.sortable || !onSortChange) return;

      if (sort?.columnId !== column.id) {
        onSortChange({ columnId: column.id, direction: 'asc' });
      } else {
        onSortChange({ columnId: column.id, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
      }
    },
    [sort, onSortChange],
  );

  const handleRowClick = useCallback(
    (row: TRow) => {
      onRowClick?.(row);
    },
    [onRowClick],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>, row: TRow) => {
      if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onRowClick(row);
      }
    },
    [onRowClick],
  );

  // Pagination derived values
  const paginationInfo = useMemo(() => {
    if (!pagination) return null;
    const { page, size, totalElements, totalPages } = pagination;
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min((page + 1) * size, totalElements);
    const isFirstPage = page === 0;
    const isLastPage = page >= totalPages - 1;
    // Build page options array for the page dropdown (1-based for display)
    const pageOptions: number[] = [];
    for (let i = 0; i < totalPages; i++) {
      pageOptions.push(i);
    }
    return { rangeStart, rangeEnd, isFirstPage, isLastPage, pageOptions };
  }, [pagination]);

  const handlePageSizeSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSize = Number(e.target.value);
      onPageSizeChange?.(newSize);
    },
    [onPageSizeChange],
  );

  const handlePageSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newPage = Number(e.target.value);
      onPageChange?.(newPage);
    },
    [onPageChange],
  );

  const handlePrevPage = useCallback(() => {
    if (pagination && pagination.page > 0) {
      onPageChange?.(pagination.page - 1);
    }
  }, [pagination, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (pagination && pagination.page < pagination.totalPages - 1) {
      onPageChange?.(pagination.page + 1);
    }
  }, [pagination, onPageChange]);

  // Loading state
  if (loading) {
    return (
      <div className={containerClass}>
        <div className={styles.statusContainer}>
          <p className={styles.statusText}>{loadingText}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    if (emptyState) {
      return <div className={containerClass}>{emptyState}</div>;
    }

    return (
      <div className={containerClass}>
        <div className={styles.statusContainer}>
          <p className={styles.statusText}>{t('K_EMPTY_NO_DATA', 'No data to display')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <LineLoader loading={fetching} />

      {/* Toolbar */}
      {showToolbar && (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={onFilterClick}
            aria-label={t('K_FILTER', 'Filter')}
            title={t('K_FILTER', 'Filter')}
          >
            <FilterIcon />
            {activeFilterCount != null && activeFilterCount > 0 && (
              <span className={styles.filterBadge}>{activeFilterCount}</span>
            )}
          </button>
        </div>
      )}

      <div className={styles.tableWrapper}>
      <table className={styles.table} aria-label={ariaLabel} role="table">
        <thead>
          <tr>
            {columns.map((column) => {
              const isSorted = sort?.columnId === column.id;

              return (
                <th
                  key={column.id}
                  style={headerStyleMap.get(column.id)}
                  className={headerClassMap.get(column.id)}
                  onClick={column.sortable ? () => handleHeaderClick(column) : undefined}
                  onKeyDown={
                    column.sortable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleHeaderClick(column);
                          }
                        }
                      : undefined
                  }
                  tabIndex={column.sortable ? 0 : undefined}
                  role={column.sortable ? 'columnheader' : undefined}
                  aria-sort={
                    isSorted
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : column.sortable
                        ? 'none'
                        : undefined
                  }
                >
                  <span className={styles.headerContent}>
                    {column.header}
                    {column.sortable && (
                      <SortIcon direction={sort?.direction} active={isSorted} />
                    )}
                  </span>
                </th>
              );
            })}
            {rowActions && <th className={styles.lastHeaderWithActions} />}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const key = rowKey(row);
            const isClickable = !!onRowClick;

            return (
              <tr
                key={key}
                className={isClickable ? styles.clickableRow : undefined}
                onClick={isClickable ? () => handleRowClick(row) : undefined}
                onKeyDown={isClickable ? (e) => handleRowKeyDown(e, row) : undefined}
                tabIndex={isClickable ? 0 : undefined}
                role={isClickable ? 'button' : undefined}
              >
                {columns.map((column) => {
                  const isTruncated = truncatedColumns.has(column.id);
                  const tdClass = [
                    column.className,
                    isTruncated ? styles.truncatedCell : undefined,
                  ].filter(Boolean).join(' ') || undefined;
                  return (
                    <td
                      key={column.id}
                      style={cellStyleMap.get(column.id)}
                      className={tdClass}
                    >
                      {column.cell(row)}
                    </td>
                  );
                })}
                {rowActions && (
                  <td
                    className={styles.lastColumnWithActions}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={styles.rowActionsOverlay}>
                      {rowActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Pagination footer */}
      {pagination && paginationInfo && (
        <div className={styles.paginationFooter} role="navigation" aria-label="Table pagination">
          {/* Left side: page size + range info */}
          <div className={styles.paginationLeft}>
            <span className={styles.paginationLabel}>{t('K_PAGINATION_SHOWING', 'Showing')}</span>
            <select
              className={styles.paginationSelect}
              value={pagination.size}
              onChange={handlePageSizeSelect}
              aria-label={t('K_PAGINATION_ITEMS_PER_PAGE', 'Items per page')}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span className={styles.paginationLabel}>{t('K_PAGINATION_ITEMS_PER_PAGE', 'items per page')}</span>
            <span className={styles.paginationDivider} aria-hidden="true">
              |
            </span>
            <span className={styles.paginationLabel}>
              {paginationInfo.rangeStart} - {paginationInfo.rangeEnd} {t('K_PAGINATION_OF', 'Of')}{' '}
              {pagination.totalElements} {t('K_PAGINATION_ITEMS', 'items')}
            </span>
          </div>

          {/* Right side: page navigation */}
          <div className={styles.paginationRight}>
            <span className={styles.paginationLabel}>{t('K_PAGINATION_PAGE', 'Page')}</span>
            <select
              className={styles.paginationSelect}
              value={pagination.page}
              onChange={handlePageSelect}
              aria-label={t('K_PAGINATION_CURRENT_PAGE', 'Current page')}
            >
              {paginationInfo.pageOptions.map((pageIdx) => (
                <option key={pageIdx} value={pageIdx}>
                  {pageIdx + 1}
                </option>
              ))}
            </select>
            <span className={styles.paginationLabel}>
              {t('K_PAGINATION_OF_TOTAL', 'of')} {pagination.totalPages}
            </span>
            <button
              type="button"
              className={styles.paginationNavButton}
              onClick={handlePrevPage}
              disabled={paginationInfo.isFirstPage}
              aria-label="Previous page"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className={styles.paginationNavButton}
              onClick={handleNextPage}
              disabled={paginationInfo.isLastPage}
              aria-label="Next page"
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(DataTable) as typeof DataTable;
